/**
 * Withdrawals — money leaving.
 *
 * The state machine is the screen: `requested → approved → processing →
 * completed | confirmed`, with `failed` and `rejected` as exits. `confirmed`
 * is the one an admin never presses: the outbound Telebirr SMS reaches the
 * forwarder and settles the payout on its own (migration 053), which is why
 * "Mark sent" stays here as the fallback for the payouts no SMS covers.
 * Each transition is one
 * button, and only the transitions legal from the current state are rendered,
 * because an admin console that offers an illegal action and then explains the
 * 409 is teaching its operators to distrust it.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { at, etb, since, shortId } from '../format.ts';
import { href, navigate, type Route } from '../router.ts';
import {
  Banner,
  ConfirmButton,
  Empty,
  ErrorBanner,
  Id,
  ReasonForm,
  Row,
  Spinner,
  Status,
} from '../components/ui.tsx';
import type { AdminView, PayoutAttemptView, AdminWithdrawalView } from '../api/types.ts';

const PAGE_SIZE = 50;

export function WithdrawalQueue({ tab }: { tab?: Extract<Route, { name: 'withdrawals' }>['tab'] }) {
  const showAll = tab === 'all';
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawalView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback((all: boolean, start: number) => {
    setWithdrawals(null);
    setError(null);
    if (!all) {
      void api
        .pendingWithdrawals()
        .then(({ withdrawals: next }) => {
          setWithdrawals(next);
          setTotal(next.length);
        })
        .catch(setError);
      return;
    }
    void api
      .withdrawals({ limit: PAGE_SIZE, offset: start })
      .then((res) => {
        setWithdrawals(res.withdrawals);
        setTotal(res.total);
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    load(showAll, offset);
  }, [load, showAll, offset]);

  // Switching tabs resets paging — "page 3 of All" makes no sense on Pending.
  useEffect(() => {
    setOffset(0);
  }, [showAll]);

  return (
    <section>
      <header className="pagehead">
        <h1>{showAll ? 'Withdrawals' : 'Withdrawals in flight'}</h1>
        {withdrawals ? <span className="count">{total}</span> : null}
      </header>

      {/*
        "In flight" drops a payout the moment it settles, which is right for a
        queue and wrong as the only list there is: a completed or confirmed
        withdrawal appeared on no screen at all. This tab is where a payout
        stays findable — for reconciling against a Telebirr statement, and for
        the player who says the money never arrived.
      */}
      <div className="tabs">
        <button
          type="button"
          className={`tabs__tab${!showAll ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'withdrawals' })}
        >
          In flight
        </button>
        <button
          type="button"
          className={`tabs__tab${showAll ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'withdrawals', tab: 'all' })}
        >
          All
        </button>
      </div>

      <ErrorBanner error={error} />
      {withdrawals === null && !error ? <Spinner /> : null}
      {withdrawals?.length === 0 ? (
        <Empty>{showAll ? 'No withdrawals yet.' : 'Nothing waiting.'}</Empty>
      ) : null}

      {withdrawals && withdrawals.length > 0 ? (
        <>
        <table className="table" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">Amount</th>
              <th scope="col" role="columnheader">Method</th>
              <th scope="col" role="columnheader">Destination</th>
              <th scope="col" role="columnheader">Player</th>
              <th scope="col" role="columnheader">Bank reference</th>
              <th scope="col" role="columnheader">{showAll ? 'Requested' : 'Waiting'}</th>
              <th scope="col" role="columnheader">Status</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {withdrawals.map((w) => (
              <tr role="row" key={w.id}>
                <td role="cell" data-label="Amount">
                  <a className="link" href={href({ name: 'withdrawal', id: w.id })}>
                    {etb(w.amount)}
                  </a>
                </td>
                <td role="cell" data-label="Method">{w.method}</td>
                <td role="cell" data-label="Destination">
                  <code>{w.destinationDetail}</code>
                </td>
                <td role="cell" data-label="Player">
                  <code className="muted">{shortId(w.userId)}</code>
                </td>
                <td role="cell" data-label="Bank reference">
                  {/* The bank's number for the outbound transfer — what a
                      Telebirr statement is reconciled against. Blank while the
                      payout is still in flight, and on anything paid before
                      migration 052. */}
                  {w.payoutTxReference ? (
                    <code>{w.payoutTxReference}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td role="cell" data-label={showAll ? 'Requested' : 'Waiting'}>{since(w.createdAt)}</td>
                <td role="cell" data-label="Status">
                  <Status value={w.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {showAll && total > PAGE_SIZE ? (
          <div className="actions">
            <button
              type="button"
              className="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ‹ Newer
            </button>
            <span className="muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <button
              type="button"
              className="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Older ›
            </button>
          </div>
        ) : null}
        </>
      ) : null}
    </section>
  );
}

export function WithdrawalDetail({ id, me }: { id: string; me: AdminView }) {
  const [withdrawal, setWithdrawal] = useState<AdminWithdrawalView | null>(null);
  const [attempts, setAttempts] = useState<PayoutAttemptView[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setWithdrawal(await api.withdrawal(id));
      // Its own call, and its own failure. The dispatcher's history is
      // context, not the record — a withdrawal that cannot show its attempts
      // is still a withdrawal an operator has to be able to act on.
      try {
        setAttempts((await api.payoutAttempts(id)).attempts);
      } catch {
        setAttempts([]);
      }
    } catch (err) {
      setError(err);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!withdrawal) {
    return (
      <section>
        <ErrorBanner error={error} />
        {!error ? <Spinner /> : null}
      </section>
    );
  }

  const w = withdrawal;

  return (
    <section className="detail">
      <header className="pagehead">
        <a className="link back" href={href({ name: 'withdrawals' })}>
          ‹ Queue
        </a>
        <h1>{etb(w.amount)}</h1>
        <Status value={w.status} />
      </header>

      <ErrorBanner error={error} />

      <PayoutAttempts
        attempts={attempts}
        status={w.status}
        busy={busy}
        canProcess={can(me.role, 'withdrawal.process')}
        onFinalize={(txReference) => void act(() => api.completeWithdrawal(w.id, txReference))}
      />

      {/*
        Stated on every screen where someone might wonder whether cancelling
        costs the player anything: the money left `available` when the request
        was created, so nothing here is "taking" it — failing or rejecting
        returns it.
      */}
      <Banner kind="ok">
        This amount is already locked out of the player's spendable balance. Rejecting or failing
        the withdrawal releases it back to them.
      </Banner>

      <div className="detail__facts">
        <Row label="Send to">
          <code>{w.destinationDetail}</code>
        </Row>
        <Row label="Method">{w.method}</Row>
        {BigInt(w.cutAmount) > 0n && (
          <>
            <Row label="Winnings (free)">{etb(w.freeAmount)}</Row>
            <Row label="Deposit cut (22%)">−{etb(w.cutAmount)}</Row>
            <Row label="Net to bank">
              {etb((BigInt(w.amount) - BigInt(w.cutAmount)).toString())}
            </Row>
          </>
        )}
        <Row label="Player">
          <Id value={w.userId} />
        </Row>
        <Row label="Requested">
          {at(w.createdAt)} <span className="muted">({since(w.createdAt)})</span>
        </Row>
        <Row label="Approved by">
          <Id value={w.reviewedBy} />
        </Row>
        <Row label="Processed by">
          <Id value={w.processedBy} />
        </Row>
        <Row label="Lock txn">
          <Id value={w.lockTransactionId} />
        </Row>
        <Row label="Payout txn">
          <Id value={w.payoutTransactionId} />
        </Row>
        {/*
          Placed right under the ledger's own payout id, because the two are
          easy to confuse and answer different questions: that one says we
          recorded the payout, this one is what reconciles against a Telebirr
          statement and what the player quotes to support. Blank on payouts
          completed before migration 052 — those genuinely have no reference,
          and showing an em dash is more honest than hiding the row.
        */}
        <Row label="Bank reference">
          {w.payoutTxReference ? <code>{w.payoutTxReference}</code> : <span className="muted">—</span>}
        </Row>
        <Row label="Release txn">
          <Id value={w.releaseTransactionId} />
        </Row>
        {w.failureReason ? <Row label="Failure">{w.failureReason}</Row> : null}
      </div>

      <div className="actions">
        {w.status === 'requested' ? (
          <>
            <ConfirmButton
              className="button button--primary"
              confirm="Approve this withdrawal for payment?"
              disabled={busy || !can(me.role, 'withdrawal.approve')}
              onConfirm={() => void act(() => api.approveWithdrawal(w.id))}
            >
              Approve
            </ConfirmButton>
            <ReasonForm
              action="Reject"
              placeholder="Why is this being rejected?"
              danger
              disabled={busy || !can(me.role, 'withdrawal.reject')}
              onSubmit={(reason) => void act(() => api.rejectWithdrawal(w.id, reason))}
            />
          </>
        ) : null}

        {w.status === 'approved' ? (
          <ConfirmButton
            className="button button--primary"
            confirm="Mark as being sent? Do this when you start the transfer."
            disabled={busy || !can(me.role, 'withdrawal.process')}
            onConfirm={() => void act(() => api.processWithdrawal(w.id))}
          >
            Start sending
          </ConfirmButton>
        ) : null}

        {w.status === 'processing' ? (
          <>
            {/*
              `process` and `complete` are two calls because `complete` posts the
              bank leg — the ledger starts asserting the money left. The
              confirmation asks the only question that matters, and asks it in
              the past tense on purpose.
            */}
            {/*
              The bank's transaction number is required to complete, so this is
              a form rather than a confirmation. It replaces the old "have you
              confirmed the transfer left?" prompt with something better: a
              question that cannot be answered yes without actually having the
              bank's receipt in front of you.
            */}
            <ReasonForm
              action="Mark sent"
              placeholder="Telebirr transaction number"
              disabled={busy || !can(me.role, 'withdrawal.process')}
              onSubmit={(txReference) => void act(() => api.completeWithdrawal(w.id, txReference))}
            />
            <p className="muted hint">
              Usually unnecessary — the Telebirr SMS confirms this payout on its own once the
              transfer goes through. Use this only if the SMS never arrives.
            </p>
            <ReasonForm
              action="Mark failed"
              placeholder="What went wrong with the transfer?"
              danger
              disabled={busy || !can(me.role, 'withdrawal.process')}
              onSubmit={(reason) => void act(() => api.failWithdrawal(w.id, reason))}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}

/**
 * What the payout robot has tried, and whether it has given up.
 *
 * `withdrawals.status` is `processing` from the moment an admin releases it
 * until the money is confirmed, and it says exactly the same thing whether a
 * phone is mid-transfer right now or abandoned the job hours ago. The API has
 * always been able to tell those apart — `GET /withdrawals/:id/payout-attempts`
 * exists and its own comment calls itself "the only place an operator can see
 * why" — but nothing in this console ever called it, so in practice nobody
 * could. A payout stalled on an empty float looked identical to one in flight,
 * which is how one sat unnoticed for an hour.
 *
 * The banner is the point of the component; the list underneath is for the
 * question that follows it.
 */
function PayoutAttempts({
  attempts,
  status,
  busy,
  canProcess,
  onFinalize,
}: {
  attempts: PayoutAttemptView[];
  status: string;
  busy: boolean;
  canProcess: boolean;
  onFinalize: (txReference: string) => void;
}) {
  if (status !== 'processing' || attempts.length === 0) return null;

  const latest = attempts[0]!;
  const reason = latest.abandonReason ?? '';
  // The float being empty is the one case with an action attached, and the
  // action is not on this screen: nothing an operator clicks here puts money
  // in the sending account. Saying so beats a generic "attention needed".
  const emptyFloat = /insufficient balance/i.test(reason);

  return (
    <>
      {latest.status === 'abandoned' ? (
        <Banner kind="warn">
          {emptyFloat ? (
            <>
              <strong>The telebirr float is empty.</strong> This payout was not sent, and the
              robot will not try it again. The player is still owed this money —{' '}
              <strong>do not mark it failed</strong>, that would hand their own balance back as
              if the request had been cancelled.
              <p className="muted hint">
                Send the transfer yourself from the telebirr app, then put its transaction number
                in here. That number is what the ledger records as proof the money left, and what
                stops the same payout being sent twice.
              </p>
              <ReasonForm
                action="Mark as finalized"
                placeholder="Telebirr transaction number"
                disabled={busy || !canProcess}
                onSubmit={onFinalize}
              />
            </>
          ) : (
            <>
              <strong>The dispatcher gave up on this payout.</strong> Nothing was sent. Reason:{' '}
              {reason || 'not recorded'}
            </>
          )}
        </Banner>
      ) : null}

      {latest.status === 'dispatched' ? (
        <Banner kind="warn">
          <strong>A transfer was started and never confirmed.</strong> The money may have moved.
          Check telebirr before doing anything here — sending again could pay twice.
        </Banner>
      ) : null}

      <details className="audit">
        <summary>Dispatcher attempts ({attempts.length})</summary>
        <ul className="broadcastlist">
          {attempts.map((a) => (
            <li key={a.id} className="broadcastlist__item">
              <div>
                #{a.attemptNo} · {a.status} · {a.dispatcher}
              </div>
              <div className="muted small">
                {new Date(a.claimedAt).toLocaleString()}
                {a.abandonReason ? ` · ${a.abandonReason}` : ''}
                {a.observedReference ? ` · ref ${a.observedReference}` : ''}
              </div>
            </li>
          ))}
        </ul>
      </details>
    </>
  );
}
